import React from 'react';
import { Row, Col, Form, Spinner } from 'react-bootstrap';
import { Search, ChevronDown } from 'lucide-react';
import { Estado, Municipio } from '../../../services/ibgeService';
import { ClienteFormData } from '../../../types';

interface CustomerAddressProps {
  formData: ClienteFormData;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void;
  loadingCEP: boolean;
  loadingMunicipios: boolean;
  estados: Estado[];
  municipioSearchTerm: string;
  showMunicipioDropdown: boolean;
  selectedMunicipio: Municipio | null;
  filteredMunicipios: Municipio[];
  municipioSearchRef: React.RefObject<HTMLDivElement>;
  handleMunicipioSearch: (term: string) => void;
  handleMunicipioSelect: (municipio: Municipio) => void;
  handleMunicipioClear: () => void;
}

const CustomerAddress: React.FC<CustomerAddressProps> = ({
  formData,
  handleChange,
  loadingCEP,
  loadingMunicipios,
  estados,
  municipioSearchTerm,
  showMunicipioDropdown,
  selectedMunicipio,
  filteredMunicipios,
  municipioSearchRef,
  handleMunicipioSearch,
  handleMunicipioSelect,
  handleMunicipioClear,
}) => {
  return (
    <>
      <Row>
        <Col xs={12} md={4}>
          <Form.Group className="mb-3">
            <Form.Label>CEP</Form.Label>
            <div className="position-relative">
              <Form.Control
                type="text"
                name="endereco.cep"
                value={formData.endereco?.cep || ''}
                onChange={handleChange}
                placeholder="00000-000"
              />
              {loadingCEP && (
                <div className="position-absolute top-50 end-0 translate-middle-y pe-3">
                  <Spinner size="sm" />
                </div>
              )}
            </div>
            <Form.Text className="text-muted">
              Digite o CEP para preenchimento automático
            </Form.Text>
          </Form.Group>
        </Col>
        <Col xs={12} md={8}>
          <Form.Group className="mb-3">
            <Form.Label>Logradouro</Form.Label>
            <Form.Control
              type="text"
              name="endereco.logradouro"
              value={formData.endereco?.logradouro || ''}
              onChange={handleChange}
              placeholder="Rua, Avenida, etc."
            />
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col xs={12} md={3}>
          <Form.Group className="mb-3">
            <Form.Label>Número</Form.Label>
            <Form.Control
              type="text"
              name="endereco.numero"
              value={formData.endereco?.numero || ''}
              onChange={handleChange}
              placeholder="123"
            />
          </Form.Group>
        </Col>
        <Col xs={12} md={9}>
          <Form.Group className="mb-3">
            <Form.Label>Complemento</Form.Label>
            <Form.Control
              type="text"
              name="endereco.complemento"
              value={formData.endereco?.complemento || ''}
              onChange={handleChange}
              placeholder="Apartamento, casa, etc."
            />
          </Form.Group>
        </Col>
      </Row>

      <Row>
        <Col xs={12} md={4}>
          <Form.Group className="mb-3">
            <Form.Label>Bairro</Form.Label>
            <Form.Control
              type="text"
              name="endereco.bairro"
              value={formData.endereco?.bairro || ''}
              onChange={handleChange}
              placeholder="Nome do bairro"
            />
          </Form.Group>
        </Col>
        <Col xs={12} md={2}>
          <Form.Group className="mb-3">
            <Form.Label>UF</Form.Label>
            <Form.Select
              name="endereco.uf"
              value={formData.endereco?.uf || ''}
              onChange={handleChange}
            >
              <option value="">Selecione</option>
              {estados.map((estado) => (
                <option key={estado.sigla} value={estado.sigla}>
                  {estado.sigla}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col xs={12} md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Cidade</Form.Label>
            <div ref={municipioSearchRef} style={{ position: 'relative' }}>
              <div style={{ position: 'relative' }}>
                <Form.Control
                  type="text"
                  placeholder={
                    !formData.endereco?.uf 
                      ? 'Selecione primeiro o estado' 
                      : loadingMunicipios 
                        ? 'Carregando municípios...' 
                        : 'Digite o nome da cidade...'
                  }
                  value={municipioSearchTerm}
                  onChange={(e) => handleMunicipioSearch(e.target.value)}
                  onFocus={() => {
                    if (formData.endereco?.uf && !loadingMunicipios) {
                      handleMunicipioSearch(municipioSearchTerm);
                    }
                  }}
                  disabled={!formData.endereco?.uf || loadingMunicipios}
                  style={{
                    paddingRight: '80px',
                    borderRadius: '8px',
                    border: selectedMunicipio ? '2px solid #0EA5E9' : '1px solid #ced4da'
                  }}
                />
                <div style={{
                  position: 'absolute',
                  right: '8px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}>
                  {loadingMunicipios ? (
                    <Spinner size="sm" />
                  ) : (
                    <>
                      <Search size={16} color="#666" />
                      {selectedMunicipio && (
                        <button
                          type="button"
                          onClick={handleMunicipioClear}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#dc3545',
                            cursor: 'pointer',
                            padding: '2px',
                            borderRadius: '4px'
                          }}
                          title="Limpar seleção"
                        >
                          ×
                        </button>
                      )}
                      <ChevronDown size={16} color="#666" />
                    </>
                  )}
                </div>
              </div>

              {/* Dropdown de resultados */}
              {showMunicipioDropdown && formData.endereco?.uf && !loadingMunicipios && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: 'white',
                  border: '1px solid #ced4da',
                  borderTop: 'none',
                  borderRadius: '0 0 8px 8px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  zIndex: 1000,
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                }}>
                  {filteredMunicipios.length > 0 ? (
                    filteredMunicipios.map(municipio => (
                      <div
                        key={municipio.id}
                        onClick={() => handleMunicipioSelect(municipio)}
                        style={{
                          padding: '12px 16px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #f0f0f0',
                          backgroundColor: 'white',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f8f9fa';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'white';
                        }}
                      >
                        <div style={{ fontWeight: '500', color: '#333' }}>
                          {municipio.nome}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{
                      padding: '16px',
                      textAlign: 'center',
                      color: '#666',
                      fontSize: '14px'
                    }}>
                      {municipioSearchTerm ? 'Nenhuma cidade encontrada' : 'Digite para buscar cidades'}
                    </div>
                  )}
                </div>
              )}
            </div>
          </Form.Group>
        </Col>
      </Row>
    </>
  );
};

export default CustomerAddress;